import {
  ChevronDown,
  ChevronUp,
  Payment,
  Withdrawal,
} from "@/components/bnp-icons";
import { Button } from "@/components/bnp-ui";
import { formatNumber } from "@/utils/formatNumber";
import { Checkbox, Skeleton } from "@mantine/core";
import React, { useState } from "react";

export interface Transaction {
  id: string;
  reference: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  provider_reference: string;
  provider_response: any[];
  createdAt: string;
}

interface TransactionsTableProps {
  transactions: Transaction[];
  isLoading: boolean;
  metadata?: {
    cursor: string;
    beforeCursor: string;
    pageNumber: number;
    totalCount: number;
    page: number;
  };
  onPageChange: (page: number) => void;
}

const SortIndicator = ({
  active,
  direction,
}: {
  active: boolean;
  direction: "asc" | "desc" | null;
}) => {
  return (
    <svg
      width="10"
      height="14"
      viewBox="0 0 10 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="ml-1"
    >
      <path
        d="M9.16732 4.91667L5.00065 0.75L0.833984 4.91667H9.16732Z"
        fill={active && direction === "asc" ? "#2D96A7" : "#868C98"}
      />
      <path
        d="M9.16732 9.08333L5.00065 13.25L0.833984 9.08333H9.16732Z"
        fill={active && direction === "desc" ? "#2D96A7" : "#868C98"}
      />
    </svg>
  );
};

const TransactionsTable = ({
  transactions,
  isLoading,
  metadata,
  onPageChange,
}: TransactionsTableProps) => {
  const [sortField, setSortField] = useState<keyof Transaction | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const handleSort = (field: keyof Transaction) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleRowSelect = (id: string) => {
    const newSelected = new Set(selectedRows);
    if (selectedRows.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === filteredTransactions.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredTransactions.map((t) => t.id)));
    }
  };

  const filteredTransactions = transactions.filter((transaction) =>
    Object.values(transaction).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  );

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    if (!sortField) return 0;

    const aValue = a[sortField];
    const bValue = b[sortField];

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortDirection === "asc"
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  // Get status color based on transaction status
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "success":
        return "bg-[#CBF5E5] text-[#1C7B44]";
      case "pending":
        return "bg-[#FEF3EB] text-[#CC7914]";
      case "failed":
        return "bg-[#F8C9D2] text-[#8C1823]";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Empty state
  if (!isLoading && (!transactions || transactions.length === 0)) {
    return (
      <div className="w-full py-16 flex flex-col items-center justify-center text-center">
        <div className="mb-4">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5Z"
              stroke="#868C98"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 7L12 13L21 7"
              stroke="#868C98"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-700">
          No transactions found
        </h3>
        <p className="text-gray-500 mt-2">
          You don&apos;t have any transactions yet.
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="rounded-[24px]">
              <tr className="text-grey-7 bg-weak-100 rounded-[24px]">
                <th className="p-4 text-left">
                  <Skeleton height={24} width={24} />
                </th>
                <th className="p-4 text-left">
                  <Skeleton height={24} width={120} />
                </th>
                <th className="p-4 text-left">
                  <Skeleton height={24} width={100} />
                </th>
                <th className="p-4 text-left">
                  <Skeleton height={24} width={80} />
                </th>
                <th className="p-4 text-left">
                  <Skeleton height={24} width={100} />
                </th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i} className="border-b">
                  <td className="p-4">
                    <Skeleton height={24} width={24} />
                  </td>
                  <td className="p-4">
                    <Skeleton height={24} width={120} />
                  </td>
                  <td className="p-4">
                    <Skeleton height={24} width={100} />
                  </td>
                  <td className="p-4">
                    <Skeleton height={24} width={80} />
                  </td>
                  <td className="p-4">
                    <Skeleton height={24} width={100} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="rounded-[24px]">
            <tr className="text-grey-7 bg-weak-100 rounded-[24px]">
              {/* <th className="p-4 text-left">
                <Checkbox
                  color="cyan"
                  checked={selectedRows.size === filteredTransactions.length}
                  onChange={handleSelectAll}
                  className="cursor-pointer"
                />
              </th> */}
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("reference")}
              >
                <span className="inline-flex items-center font-normal">
                  Reference
                  <SortIndicator
                    active={sortField === "reference"}
                    direction={sortField === "reference" ? sortDirection : null}
                  />
                </span>
              </th>
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("amount")}
              >
                <span className="inline-flex items-center font-normal">
                  Amount
                  <SortIndicator
                    active={sortField === "amount"}
                    direction={sortField === "amount" ? sortDirection : null}
                  />
                </span>
              </th>
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("status")}
              >
                <span className="inline-flex items-center font-normal">
                  Status
                  <SortIndicator
                    active={sortField === "status"}
                    direction={sortField === "status" ? sortDirection : null}
                  />
                </span>
              </th>
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("createdAt")}
              >
                <span className="inline-flex items-center font-normal">
                  Date
                  <SortIndicator
                    active={sortField === "createdAt"}
                    direction={sortField === "createdAt" ? sortDirection : null}
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTransactions.map((transaction) => (
              <tr key={transaction.id} className="border-b hover:bg-gray-50">
                {/* <td className="p-4">
                  <Checkbox
                    color="cyan"
                    checked={selectedRows.has(transaction.id)}
                    onChange={() => handleRowSelect(transaction.id)}
                    className="cursor-pointer"
                  />
                </td> */}
                <td className="p-4 text-main-900 font-medium">
                  {transaction.reference}
                </td>
                <td className="p-4 text-grey-7">
                  <span className="font-semibold">₦</span>
                  {formatNumber(Math.abs(transaction.amount).toFixed(2))}
                </td>
                <td className="p-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      transaction.status,
                    )}`}
                  >
                    {transaction.status}
                  </span>
                </td>
                <td className="p-4 text-grey-7">
                  {formatDate(transaction.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* {metadata && (
        <div className="flex items-center justify-between mt-4 px-4">
          <div className="text-sm text-gray-700">
            Showing page {metadata.pageNumber} of {metadata?.totalCount}
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={() => onPageChange(Math.max(1, metadata.pageNumber - 1))}
              disabled={metadata.pageNumber === 1}
              className={`px-3 py-1 rounded-[8px] ${
                metadata.pageNumber === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-bnp-dark text-white"
              }`}
            >
              Previous
            </Button>
            <Button
              onClick={() => onPageChange(metadata.pageNumber + 1)}
              className="px-3 py-1 rounded-[8px] bg-bnp-dark text-white"
            >
              Next
            </Button>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default TransactionsTable;
